import {
  Match,
  Show,
  Switch,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { ExpandAnimationWrapper } from "@/components/Base/AnimationWrapper";
import { Button } from "@/components/Base/Button/Button";
import LazyImg from "@/components/Base/LazyImg";
import Popup from "@/components/Base/Popup/Popup";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import Icon from "@/components/Icon";
import { WalletType } from "@/enum/component.ts";
import { useRouter } from "@/hooks/useRouter";
import { walletModel } from "@/hooks/wallet/useWallet";
import { t } from "@/i18n";
import { formatToBindPoint, getCurPointSymbol, isFiat } from "@/lib/pointUtils";
import { isGameRoute } from "@/lib/routeUtils";
import { getCryptoName } from "@/stores/cryptoStore";
import { getUserInfo, userStore } from "@/stores/userStore";
import { walletStore } from "@/stores/walletStore";

export default function HeaderWallet() {
  const isGame = isGameRoute();
  const { showDialog } = useDialog();
  const [isDrawerOpen, setIsDrawerOpen] = createSignal(false);
  const [isDirectionOpen, setIsDirectionOpen] = createSignal(false);
  // const [dropdownPosition, setDropdownPosition] = createSignal({
  //   top: 0,
  //   left: 0,
  // });
  const { push, toDeposit } = useRouter();

  let popupRef: { handleClose: () => void } | undefined;

  const handleCurrency = () => {
    setIsDirectionOpen(!isDirectionOpen());
    if (!isDrawerOpen()) {
      setIsDrawerOpen(!isDrawerOpen());
      getUserInfo();
    } else {
      if (popupRef) {
        popupRef.handleClose();
      }
    }
  };
  let dropdownRef: HTMLElement;
  const handleOutsideClick = (e: MouseEvent) => {
    if (!isDrawerOpen()) return;
    const target = e.target as Node;
    if (dropdownRef && !dropdownRef.contains(target)) {
      if (popupRef) {
        popupRef.handleClose();
      } else {
        setIsDrawerOpen(false);
      }
      setIsDirectionOpen(false);
    }
  };
  onMount(() => {
    document.addEventListener("click", handleOutsideClick);
  });

  onCleanup(() => {
    document.removeEventListener("click", handleOutsideClick);
  });

  return (
    <Show when={userStore.pointType} fallback={<></>}>
      <div class="relative" ref={(el) => (dropdownRef = el)}>
        <div
          id="h5-wallet-dropdown-trigger"
          class="bg-up-icondi border-general-sk flex h-[34px] items-center rounded-[6px] border p-[2px]"
        >
          <div class="mr-[6px] flex items-center" onClick={handleCurrency}>
            <div class="mx-[4px] h-[14px] w-[14px]">
              <LazyImg
                src={walletStore.selectedWallet?.icon}
                alt={walletStore.selectedWallet?.pointName}
                class="h-[14px] w-[14px] rounded-full object-cover"
              />
            </div>

            {/* fix:https://zendao.baidu.date/task-view-9162.html */}
            {/*<Show
              when={!isGame}
              fallback={
                <div class="text-general-light font-Inter text-[12px] font-bold">
                  {getCryptoName(userStore.pointType)}
                </div>
              }
            >*/}
            <div
              class="flex flex-col items-start justify-start"
              classList={{ "mr-[8px]": walletModel() === WalletType.bonus }}
            >
              {/* 积分 */}
              <div class="text-general-light font-Inter text-[12px] font-[700] leading-[15px]">
                {
                  formatToBindPoint({
                    value: walletStore.selectedWallet?.point,
                    pointType: userStore.pointType || 0,
                    showSymbol: false,
                    isFiatPoint: isFiat(userStore.pointType || 0),
                  }) as string
                }
              </div>

              {/* 奖金 */}
              <Show when={walletModel() === WalletType.bonus} fallback={<></>}>
                <div class="text-general-mid-warning font-Inter text-[11px] font-[700] leading-[13px]">
                  {
                    formatToBindPoint({
                      value: walletStore.selectedWallet?.bonus,
                      pointType: userStore.pointType || 0,
                      showSymbol: false,
                      isFiatPoint: isFiat(userStore.pointType || 0),
                    }) as string
                  }
                </div>
              </Show>
            </div>
            {/*</Show>*/}

            <Show when={walletModel() === WalletType.bonus} fallback={<></>}>
              <Icon
                name="general/arrow_right"
                class={`text-general-iconlight size-[12px] transition-transform duration-300 ${isDirectionOpen() ? "-rotate-90" : "rotate-90"}`}
              />
            </Show>
          </div>

          <Button
            variant={"primary"}
            class="flex !h-[30px] !min-w-[58px] items-center justify-center rounded-[4px] px-[6px] text-[12px] font-[700]"
            onClick={() => toDeposit()}
          >
            {/* <Icon
              name="general/add_to"
              class="text-general-butzi size-[10px]"
            /> */}
            {t("affiliate.deposit")}
          </Button>
        </div>

        {/* Wallet popup using top-sliding Popup component */}
        <Popup
          show={isDrawerOpen()}
          onClose={() => setIsDrawerOpen(false)}
          direction="top"
          withHeader={false}
          zIndex={49}
          offset={48}
          withPadding={false}
          contentClass="max-h-[400px]"
          ref={(ref) => (popupRef = ref)}
        >
          <div class="bg-general-bg3 flex flex-col gap-[10px] overflow-y-auto rounded-b-[10px] px-[12px] pb-[15px] pt-[10px] shadow-lg">
            {/* PC版本的Points部分 */}
            <div
              class="flex h-[15px] items-center gap-[4px]"
              onclick={() => {
                document.body.style.overflow = "hidden";
                push("bonus-rule");
                setIsDrawerOpen(false);
              }}
            >
              <span class="text-general-light text-[12px] font-[600] leading-normal">
                {t("reward.points")}
              </span>
              <div class="cursor-pointer">
                <Icon name="exclamation-mark1" class="size-[14px]" />
              </div>
            </div>

            {/* PC版本的积分显示区域 */}
            <div class="border-1 border-general-sk flex items-center justify-center rounded-[8px] border-dashed py-[11px]">
              <div class="flex flex-col">
                <span class="text-general-money text-center text-[22px] font-[600] leading-normal">
                  {getCurPointSymbol()}
                  {
                    formatToBindPoint({
                      value: walletStore.selectedWallet?.bonus,
                      pointType: walletStore.selectedWallet?.pointType || 0,
                      showSymbol: false,
                      isFiatPoint: isFiat(
                        walletStore.selectedWallet?.pointType || 0,
                      ),
                    }) as string
                  }
                </span>
                <Switch>
                  <Match when={walletStore.pointStatus === "rollover"}>
                    <span class="text-general-darkzi text-[12px] font-[500] leading-normal">
                      {t("betmatch.wager")}{" "}
                      <span class="text-general-light text-[13px]">
                        {getCurPointSymbol()}
                        {
                          formatToBindPoint({
                            value: walletStore.selectedWallet?.bonusCode,
                            pointType:
                              walletStore.selectedWallet?.pointType || 0,
                            showSymbol: false,
                            isFiatPoint: isFiat(
                              walletStore.selectedWallet?.pointType || 0,
                            ),
                          }) as string
                        }{" "}
                      </span>
                      {t("withdraw.redeemablepoints")}
                    </span>
                  </Match>
                  <Match when={walletStore.pointStatus === "redeemable"}>
                    <span class="text-general-darkzi text-[12px] font-[500] leading-normal">
                      {t("withdraw.points_redeemable_tip")}
                    </span>
                  </Match>
                  <Match when={walletStore.pointStatus === "none"}>
                    <span class="text-general-darkzi text-[12px] font-[500] leading-normal">
                      {t("withdraw.earn_points_event_tip")}
                    </span>
                  </Match>
                </Switch>
              </div>
            </div>

            <div class="bg-general-tmd10 flex h-[36px] items-center justify-center gap-[6px] rounded-[8px]">
              <Switch>
                <Match when={walletStore.pointStatus === "rollover"}>
                  <div
                    class="flex items-center gap-1"
                    onclick={() => {
                      push("/wallet/rollover");
                      setIsDrawerOpen(false);
                    }}
                  >
                    <span class="text-general-light text-[12px] font-[500] leading-normal">
                      {t("withdraw.rollover_details")}
                    </span>
                    <Icon
                      name="double-chevron"
                      class="text-general-light size-[12px]"
                    />
                  </div>
                </Match>
                <Match when={walletStore.pointStatus === "redeemable"}>
                  <div
                    class="flex items-center gap-1"
                    onclick={() => {
                      push("bonus-center");
                      setIsDrawerOpen(false);
                    }}
                  >
                    <span class="text-general-highbut text-[12px] font-[500] leading-normal">
                      {t("withdraw.points_redeemable_now")}
                    </span>
                    <Icon
                      name="double-chevron"
                      class="text-general-highbut size-[12px]"
                    />
                  </div>
                </Match>
                <Match when={walletStore.pointStatus === "none"}>
                  <div
                    class="flex items-center gap-1"
                    onclick={() => {
                      push("bonus-center");
                      setIsDrawerOpen(false);
                    }}
                  >
                    <span class="text-general-light text-[12px] font-[500] leading-normal">
                      {t("withdraw.earn_points")}
                    </span>
                    <Icon
                      name="double-chevron"
                      class="text-general-light size-[12px]"
                    />
                  </div>
                </Match>
              </Switch>
            </div>
          </div>
        </Popup>
      </div>
    </Show>
  );
}
